import React from "react";
import { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import { Popover } from "@mui/material";
import SearchInput from "../Constants/search_input";
import { fontFamily } from "../Constants/constants";
import { screenHeaderStyle } from "../Constants/constants";
import { useSharedValue } from "../Constants/constants";
import LoaderIndicator from "../../Utility/loading";
import "../../App.css";
import "../Constants/config.css";
import {
  getUserFileAuditByUserId,
  getUserIdFromLocalStorage,
  getReport,
} from "../../API_Endpoints/APIEndpoints";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import downloadFileIcon from "../../assets/new icons/reports/download file.svg";
import eyeIcon from "../../assets/new icons/eye.svg";
import { PoweredByRow } from "../../utils/paginationUtils";
import trashIcon from "../../assets/new icons/trash.svg";
import pdfIcon from "../../assets/new icons/reports/pdf.svg";
import excelIcon from "../../assets/new icons/reports/excel.svg";
import calendarIcon from "../../assets/new icons/dashboard/calendar.svg";
import downArrowIcon from "../../assets/new icons/arrow.svg";
import backArrowIcon from "../../assets/new icons/extraction review/back arrow.svg";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useHistory } from "react-router-dom";

const getDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "7d": {
      const s = new Date(today);
      s.setDate(s.getDate() - 6);
      return { start: s, end: now };
    }
    case "30d": {
      const s = new Date(today);
      s.setDate(s.getDate() - 29);
      return { start: s, end: now };
    }
    case "month":
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    case "year":
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    default:
      return { start: null, end: null };
  }
};

const DATE_PRESETS = [
  { label: "All Time", value: "all" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "This Month", value: "month" },
  { label: "This Year", value: "year" },
  { label: "Custom", value: "custom" },
];

export default function ReportsProcess() {
  const viewAuditColumnsData = [
    { field: "fileName", headerName: "FILE NAME", width: 150 },
    { field: "userName", headerName: "USER NAME", width: 150 },
    { field: "email", headerName: "EMAIL", width: 150 },
    { field: "action", headerName: "ACTION", width: 150 },
    { field: "actionDate", headerName: "ACTION DATE", width: 150 },
  ];

  // For the Extraction Report view columns:
  const viewExportedColumnsData = [
    { field: "File Name", headerName: "FILE NAME", width: 150 },
    { field: "Created By", headerName: "CREATED BY", width: 150 },
    { field: "Created Date", headerName: "CREATED DATE", width: 150 },
    { field: "Organization Name", headerName: "ORGANIZATION NAME", width: 150 },
    { field: "Type", headerName: "TYPE", width: 120 },
    { field: "Total Pages", headerName: "TOTAL PAGES", width: 120 },
    { field: "Total Forms", headerName: "TOTAL FORMS", width: 120 },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const { defaultBusinessunit, defaultOrganization, defaultUser } = useSharedValue();
  const { isMenuOpen } = useSharedValue();
  const history = useHistory();

  // User role state - kept for compatibility but role checking is now done in centralized function
  const [userRole, setUserRole] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Helper functions to get effective user ID and organization ID
  const getEffectiveUserId = () => {
    // Check if defaultUser exists and has a userId, otherwise use current user
    return defaultUser && defaultUser.id ? defaultUser.id : getUserIdFromLocalStorage().userId;
  };

  const getEffectiveOrganizationId = () => {
    // Check if defaultOrganization exists and has an organizationId, otherwise use current organization
    return defaultOrganization && defaultOrganization.id
      ? defaultOrganization.id
      : getUserIdFromLocalStorage().organizationId;
  };

  const [loading, setLoading] = useState(true);
  const [reportDataForView, setReportDataForView] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [viewingReport, setViewingReport] = useState(false);
  const [viewingReportName, setViewingReportName] = useState(null);

  const [viewColumns, setViewColumns] = useState([]);

  // Search functionality states
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCategory, setSearchCategory] = useState("email");
  const [availableEmails, setAvailableEmails] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  // Initialize rows state
  const [rows, setRows] = useState([
    { id: 1, reportName: "User Audit Report" },
    { id: 2, reportName: "Extraction Report" },
  ]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [pendingRange, setPendingRange] = useState([null, null]);
  const [datePreset, setDatePreset] = useState("all");

  const handleDatePresetChange = useCallback((preset) => {
    setDatePreset(preset);
    const { start, end } = getDateRange(preset);
    setStartDate(start);
    setEndDate(end);
    setPendingRange([start, end]);
    setCalendarAnchor(null);
  }, []);

  const formatDateLabel = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : null;
  const [filteredByDateData, setFilteredByDateData] = useState([]);

  // Add useEffect to handle initial loading
  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (viewingReportName) {
      handleViewReport(viewingReportName);
    }
  }, [viewingReportName, defaultUser, defaultOrganization]);
  // Using centralized getReport function from APIEndpoints.js

  // Extract unique emails and organizations when report data is loaded
  useEffect(() => {
    if (reportDataForView.length > 0) {
      // Extract unique emails
      const emails = new Set();
      reportDataForView.forEach((item) => {
        if (item.email) emails.add(item.email);
        if (item.EMAIL) emails.add(item.EMAIL);
      });
      setAvailableEmails(Array.from(emails).filter(Boolean));

      // // Extract unique organization names
      // const organizations = new Set();
      // reportDataForView.forEach(item => {
      //     if (item.organizationName) organizations.add(item.organizationName);
      //     if (item["Organization Name"]) organizations.add(item["Organization Name"]);
      //     // Also check for Created By field which might contain organization info
      //     if (item["Created By"]) organizations.add(item["Created By"]);

      // });
      // setAvailableOrganizations(Array.from(organizations).filter(Boolean));

      // Set filtered data initially to all data
      setFilteredReportData(reportDataForView);
    }
  }, [reportDataForView]);

  // Update dropdown options based on search category
  useEffect(() => {
    if (searchCategory === "email") {
      setDropdownOptions(availableEmails);
    } else {
      setDropdownOptions(availableOrganizations);
    }
  }, [searchCategory, availableEmails, availableOrganizations]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "Unknown") return dateString || "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
  };
  const handlePdfReport = async (reportName) => {
    try {
      if (reportName === "User Audit Report") {
        const actualReportName = reportName;
        // Prefer already loaded data (icon should behave like button/view)
        let data =
          Array.isArray(filteredReportData) && filteredReportData.length > 0
            ? filteredReportData
            : Array.isArray(reportDataForView) && reportDataForView.length > 0
              ? reportDataForView
              : null;

        // Fallback: fetch fresh data if nothing loaded yet
        if (!data) {
          const effectiveUserId = getEffectiveUserId();
          const effectiveOrganizationId = getEffectiveOrganizationId();
          const userAuditData = await getUserFileAuditByUserId(
            effectiveUserId,
            effectiveOrganizationId
          );
          data = userAuditData;
        }

        // Make sure we have data
        if (data.length === 0) {
          alert("No data available to generate PDF");
          return;
        }

        // Generate PDF with proper data
        const doc = generatePDF(data, actualReportName);
        doc.save(`${actualReportName}_${new Date().toISOString().split("T")[0]}.pdf`);
      } else if (reportName === "Extraction Report") {
        console.log("Calling getReport with:", {
          defaultUser,
          defaultOrganization,
        });
        const result = await getReport(defaultUser, defaultOrganization);
        console.log("getReport result:", result);
        if (result.success) {
          // Build a robust data list for PDF generation (mirrors view logic with fallbacks)
          const dataList = result.data.map((item, index) => {
            let jsonData;
            try {
              jsonData = item.json ? JSON.parse(item.json) : item;
            } catch {
              // item.json is truncated in the DB — fall back to the item's own fields
              jsonData = item;
            }

            const totalPages =
              jsonData["Total Pages"] ||
              jsonData["totalPages"] ||
              jsonData.totalPage ||
              item.totalPage ||
              item.totalPages ||
              "N/A";

            const totalForms =
              jsonData["Total Forms"] ||
              jsonData["totalForms"] ||
              jsonData.totalForms ||
              item.totalForms ||
              item.totalForm ||
              "N/A";

            // Keep remaining keys except totals
            const remaining =
              typeof jsonData === "object" && jsonData !== null
                ? Object.fromEntries(
                    Object.entries(jsonData).filter(
                      ([key]) => key !== "Total Pages" && key !== "Total Forms"
                    )
                  )
                : {};

            return {
              "File Name": item.fileName,
              "Created By": item.createdBy,
              "Created Date": formatDate(item.createdDate),
              "Organization Name": item.organizationName || "Unknown",
              "Total Pages": totalPages,
              "Total Forms": totalForms,
              ...remaining,
            };
          });

          const actualReportName = reportName;

          if (dataList.length === 0) {
            alert("No data available to generate PDF");
            return;
          }

          const doc = generatePDF(dataList, actualReportName);
          doc.save(`${actualReportName}_${new Date().toISOString().split("T")[0]}.pdf`);
        } else {
          throw new Error("Failed to retrieve Extraction Report");
        }
      }
    } catch (error) {
      console.error("Error viewing report:", error);
    }
  };

  // Excel icon: behave like PDF icon (load data then download locally as CSV)
  const handleExcelReport = async (reportName) => {
    try {
      if (reportName === "User Audit Report") {
        const actualReportName = reportName;

        // Start with full dataset
        let data = reportDataForView;

        // Prioritize filtered data if available and different from full dataset
        if (
          Array.isArray(filteredReportData) &&
          filteredReportData.length > 0 &&
          filteredReportData.length !== reportDataForView.length
        ) {
          console.log(
            "🔍 Excel User Audit - Using filtered data:",
            filteredReportData.length,
            "records"
          );
          data = filteredReportData;
        } else if (
          Array.isArray(filteredByDateData) &&
          filteredByDateData.length > 0 &&
          filteredByDateData.length !== reportDataForView.length
        ) {
          console.log(
            "🔍 Excel User Audit - Using date filtered data:",
            filteredByDateData.length,
            "records"
          );
          data = filteredByDateData;
        } else {
          console.log(
            "🔍 Excel User Audit - Using full dataset:",
            reportDataForView.length,
            "records"
          );
        }

        // Fallback fetch if nothing loaded
        if (!data || data.length === 0) {
          const effectiveUserId = getEffectiveUserId();
          const effectiveOrganizationId = getEffectiveOrganizationId();
          data = await getUserFileAuditByUserId(effectiveUserId, effectiveOrganizationId);
        }

        if (!data || data.length === 0) {
          alert("No data available to generate Excel");
          return;
        }

        const csv = generateCSV(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${actualReportName}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (reportName === "Extraction Report") {
        // Reuse the exact same data processing logic as handleViewReport
        const result = await getReport(defaultUser, defaultOrganization);
        if (!result || !result.success || !Array.isArray(result.data)) {
          alert("Failed to load Extraction Report data");
          return;
        }

        // Build data list using the same production logic as the view
        const dataList = result.data.map((item, index) => {
          let jsonData = {};
          try {
            // Check if item.json exists and is not empty
            if (item.json && typeof item.json === "string" && item.json.trim() !== "") {
              jsonData = JSON.parse(item.json);
            } else {
              console.warn(`⚠️ Excel - Empty or missing JSON for item ${index}, using item data`);
              jsonData = item;
            }
          } catch (error) {
            console.error(`❌ Excel - Failed to parse JSON for item ${index}:`, error);
            console.error(`❌ Excel - Raw item.json:`, item.json);
            jsonData = item;
          }

          // Extract total pages and forms with multiple fallback options
          const totalPages =
            jsonData["Total Pages"] ||
            jsonData["totalPages"] ||
            jsonData.totalPage ||
            item.totalPage ||
            item.totalPages ||
            "N/A";

          const totalForms =
            jsonData["Total Forms"] ||
            jsonData["totalForms"] ||
            jsonData.totalForms ||
            item.totalForms ||
            item.totalForm ||
            "N/A";

          // Extract type information and capitalize first letter
          const rawType =
            jsonData["Type"] ||
            jsonData.type ||
            item.type ||
            item.processType ||
            item.extractionType ||
            "extraction";

          const extractionType = rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();

          return {
            "File Name": item.fileName || "Unknown",
            "Created By": item.createdBy || "Unknown",
            "Created Date": formatDate(item.createdDate) || "Unknown",
            "Organization Name": item.organizationName || "Unknown",
            Type: extractionType,
            "Total Pages": totalPages,
            "Total Forms": totalForms,
            ...Object.fromEntries(
              Object.entries(jsonData || {}).filter(
                ([key]) => key !== "Total Pages" && key !== "Total Forms"
              )
            ),
          };
        });

        // Apply filters if user has filtered the view
        let exportData = dataList;

        // Check if we have filtered data and it's different from the full dataset
        if (
          Array.isArray(filteredReportData) &&
          filteredReportData.length > 0 &&
          filteredReportData.length !== dataList.length
        ) {
          console.log("🔍 Excel - Using filtered data:", filteredReportData.length, "records");
          exportData = filteredReportData;
        } else if (
          Array.isArray(filteredByDateData) &&
          filteredByDateData.length > 0 &&
          filteredByDateData.length !== dataList.length
        ) {
          console.log("🔍 Excel - Using date filtered data:", filteredByDateData.length, "records");
          exportData = filteredByDateData;
        } else {
          console.log("🔍 Excel - Using full dataset:", dataList.length, "records");
        }

        if (!exportData || exportData.length === 0) {
          alert("No data available to generate Excel");
          return;
        }

        // Append totals row like the view does
        const totals = calculateTotals(exportData);
        const finalData = totals ? [...exportData, totals] : exportData;

        const csv = generateCSV(finalData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Extraction Report_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Error generating Excel:", e);
      alert("Failed to generate Excel. Please try again.");
    }
  };

  const handleViewReport = async (reportName) => {
    setViewingReport(true);
    setIsLoading(true);

    try {
      if (reportName === "User Audit Report") {
        setViewingReportName("User Audit Report");
        const effectiveUserId = getEffectiveUserId();
        const effectiveOrganizationId = getEffectiveOrganizationId();
        const userAuditData = await getUserFileAuditByUserId(
          effectiveUserId,
          effectiveOrganizationId
        );
        console.log("User Audit Data:", userAuditData); // Debug log
        setReportDataForView(userAuditData);
        setFilteredReportData(userAuditData);
        setViewColumns(viewAuditColumnsData);
      } else if (reportName === "Extraction Report") {
        setViewingReportName("Extraction Report");
        const result = await getReport(defaultUser, defaultOrganization);
        console.log("🔍 Report Screen - getReport result:", result);
        if (result.success) {
          console.log("🔍 Report Screen - result.data:", result.data);
          console.log(
            "🔍 Report Screen - result.data length:",
            result.data ? result.data.length : 0
          );

          // Create a data list for viewing
          const dataList = result.data.map((item, index) => {
            let jsonData = {};
            if (item.json && typeof item.json === "string" && item.json.trim() !== "") {
              try {
                jsonData = JSON.parse(item.json);
              } catch (error) {
                console.warn(
                  `⚠️ Report Screen - Failed to parse JSON for item ${index}, using empty object`
                );
              }
            } else if (item.json && typeof item.json === "object") {
              jsonData = item.json;
            }

            // Extract total pages and forms with multiple fallback options
            const totalPages =
              jsonData["Total Pages"] ||
              jsonData["totalPages"] ||
              jsonData.totalPage ||
              item.totalPage ||
              item.totalPages ||
              "N/A";

            const totalForms =
              jsonData["Total Forms"] ||
              jsonData["totalForms"] ||
              jsonData.totalForms ||
              item.totalForms ||
              item.totalForm ||
              "N/A";

            // Extract type information and capitalize first letter
            const rawType =
              jsonData["Type"] ||
              jsonData.type ||
              item.type ||
              item.processType ||
              item.extractionType ||
              "extraction";

            const extractionType =
              typeof rawType === "string" && rawType.length > 0
                ? rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase()
                : "Extraction";

            return {
              "File Name": item.fileName,
              "Created By": item.createdBy,
              "Created Date": formatDate(item.createdDate),
              "Organization Name": item.organizationName || "Unknown",
              Type: extractionType,
              "Total Pages": totalPages,
              "Total Forms": totalForms,
              ...Object.fromEntries(
                Object.entries(jsonData).filter(
                  ([key]) => key !== "Total Pages" && key !== "Total Forms"
                )
              ),
            };
          });

          // Get all possible keys for columns
          const allKeys = new Set();
          dataList.forEach((item) => {
            Object.keys(item).forEach((key) => allKeys.add(key));
          });

          // Create columns, prioritizing certain fields
          const priorityFields = [
            "File Name",
            "Created By",
            "Organization Name",
            "Created Date",
            "Type",
            "Total Pages",
            "Total Forms",
          ];
          const otherFields = [...allKeys].filter((field) => !priorityFields.includes(field));

          const viewColumnsData = [
            ...priorityFields.map((field) => ({
              field,
              headerName: field.toUpperCase(),
              width: 150,
            })),
            ...otherFields.map((field) => ({
              field,
              headerName: field.toUpperCase(),
              width: 150,
            })),
          ];

          setViewColumns(viewColumnsData);
          setReportDataForView(dataList);
          setFilteredReportData(dataList);
        } else {
          console.error("❌ Report Screen - getReport failed:", result);
          console.error("❌ Report Screen - getReport error:", result.error);
          setReportDataForView([]);
          setFilteredReportData([]);
          setViewColumns(viewExportedColumnsData);
          throw new Error("Failed to retrieve Extraction Report");
        }
      }
    } catch (error) {
      console.error("❌ Report Screen - Error viewing report:", error);
      // Set empty data when there's an exception
      setReportDataForView([]);
      setFilteredReportData([]);
      if (reportName === "Extraction Report") {
        setViewColumns(viewExportedColumnsData);
      } else {
        setViewColumns(viewAuditColumnsData);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Date formatter function to convert dates to DD/MM/YYYY format

  // Enhanced function to calculate totals for Extraction Report
  const calculateTotals = (data) => {
    if (data.length === 0) return null;

    // Initialize totals object with standard fields
    const totals = {
      "File Name": "TOTALS",
      "Created By": "",
      "Created Date": "",
      "Organization Name": "",
      Type: "", // Empty string for Type column in totals
      "Total Pages": 0,
      "Total Forms": 0,
    };

    // First, collect all possible form type keys from ALL rows
    const formTypeKeys = new Set();
    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        // If it's not one of the standard fields and seems to be a form type
        if (
          ![
            "File Name",
            "Created By",
            "Created Date",
            "Organization Name",
            "Type",
            "Total Pages",
            "Total Forms",
          ].includes(key)
        ) {
          formTypeKeys.add(key);
        }
      });
    });

    // Initialize all form type counters to 0
    formTypeKeys.forEach((key) => {
      totals[key] = 0;
    });

    // Calculate sums for all numeric fields across all rows
    data.forEach((row) => {
      // Sum standard numeric fields
      if (row["Total Pages"]) {
        const pages = parseInt(row["Total Pages"]);
        if (!isNaN(pages)) totals["Total Pages"] += pages;
      }

      if (row["Total Forms"]) {
        const forms = parseInt(row["Total Forms"]);
        if (!isNaN(forms)) totals["Total Forms"] += forms;
      }

      // Sum all form types
      formTypeKeys.forEach((key) => {
        if (row[key]) {
          // Only attempt to parse if it seems to be a numeric value
          const value =
            typeof row[key] === "string" && row[key].includes("page")
              ? parseInt(row[key])
              : parseInt(row[key]);

          if (!isNaN(value)) {
            totals[key] += value;
          }
        }
      });
    });

    return totals;
  };

  const handleDownloadExcelReport = (reportName, useFiltered = false) => {
    setIsLoading(true);
    console.log("Report to download:", reportName, "Using filtered data:", useFiltered);

    const dateFields = new Set(["actionDate"]);

    // Helper function to generate and download CSV file
    const downloadCSVFile = (data, filename) => {
      try {
        // Generate CSV content
        const csv = generateCSV(data);

        // Create blob from CSV content
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        // Create download link and trigger download
        const downloadUrl = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = downloadUrl;
        downloadLink.download = `${filename}.csv`;

        // Append to body, click, and remove
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Clean up the object URL
        window.URL.revokeObjectURL(downloadUrl);

        const downloadTime = new Date();
        localStorage.setItem("downloadTime", downloadTime);
        console.log(`${reportName} downloaded successfully: ${filename}.csv`);
        setIsLoading(false);
      } catch (error) {
        console.error("Error generating CSV file:", error);
        setIsLoading(false);
      }
    };

    if (reportName === "User Audit Report") {
      // User Audit Report code
      const effectiveUserId = getEffectiveUserId();
      const effectiveOrganizationId = getEffectiveOrganizationId();

      getUserFileAuditByUserId(effectiveUserId, effectiveOrganizationId)
        .then((data) => {
          // If filtered data should be used and is available, use filteredReportData directly
          let userAuditList = data;

          if (useFiltered) {
            // Check if we have filtered data and it's different from the full dataset
            if (
              Array.isArray(filteredReportData) &&
              filteredReportData.length > 0 &&
              filteredReportData.length !== data.length
            ) {
              console.log(
                "🔍 Excel Download - Using filtered data:",
                filteredReportData.length,
                "records"
              );
              userAuditList = filteredReportData;
            } else if (
              Array.isArray(filteredByDateData) &&
              filteredByDateData.length > 0 &&
              filteredByDateData.length !== data.length
            ) {
              console.log(
                "🔍 Excel Download - Using date filtered data:",
                filteredByDateData.length,
                "records"
              );
              userAuditList = filteredByDateData;
            } else {
              console.log(
                "🔍 Excel Download - No filters applied, using full dataset:",
                data.length,
                "records"
              );
            }
          }

          // Format and transform the data
          userAuditList = userAuditList.map((item) => {
            const formattedItem = {};

            // Process each field in the item
            Object.keys(item).forEach((key) => {
              // Get the display name for the column or use the original key
              const displayKey = viewAuditColumnsData[key] || key;

              // Format date fields
              if (dateFields.has(key) && item[key]) {
                formattedItem[displayKey] = formatDate(item[key]);
              } else {
                formattedItem[displayKey] = item[key];
              }
            });

            return formattedItem;
          });

          // Create filename with current date and time
          const now = new Date();
          const dateTimeString = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
          const fileName = `user_audit_report_${dateTimeString}`;

          // Generate and download CSV file directly
          downloadCSVFile(userAuditList, fileName);
        })
        .catch((error) => {
          console.error("Error fetching User Audit Report data:", error);
          setIsLoading(false);
        });
    } else if (reportName === "Extraction Report") {
      // Extraction Report handling
      if (useFiltered) {
        // Determine which filtered data to use
        let dataToUse = filteredReportData;

        // Check if we have filtered data and it's different from the full dataset
        if (
          Array.isArray(filteredReportData) &&
          filteredReportData.length > 0 &&
          filteredReportData.length !== reportDataForView.length
        ) {
          console.log(
            "🔍 Excel Download Extraction - Using search filtered data:",
            filteredReportData.length,
            "records"
          );
          dataToUse = filteredReportData;
        } else if (
          Array.isArray(filteredByDateData) &&
          filteredByDateData.length > 0 &&
          filteredByDateData.length !== reportDataForView.length
        ) {
          console.log(
            "🔍 Excel Download Extraction - Using date filtered data:",
            filteredByDateData.length,
            "records"
          );
          dataToUse = filteredByDateData;
        } else {
          console.log("🔍 Excel Download Extraction - No filters applied, using full dataset");
          dataToUse = reportDataForView;
        }

        // Process filtered data
        // First extract all possible keys to ensure complete headers
        const allKeys = new Set();
        dataToUse.forEach((item) => {
          Object.keys(item).forEach((key) => {
            allKeys.add(key);
          });
        });

        // Format date fields in the filtered data
        const formattedData = dataToUse.map((item) => {
          const formattedItem = {};

          // Ensure all keys are present in each item (for consistent columns)
          Array.from(allKeys).forEach((key) => {
            // Format date fields
            if (dateFields.has(key) && item[key]) {
              formattedItem[key] = formatDate(item[key]);
            } else {
              formattedItem[key] = item[key] || ""; // Use empty string for missing values
            }
          });

          return formattedItem;
        });

        // Calculate totals for filtered data
        const totals = calculateTotals(formattedData);

        // Append totals row to the filtered data
        const dataWithTotals = [...formattedData];
        if (totals) dataWithTotals.push(totals);

        // Create filename with current date and time
        const now = new Date();
        const dateTimeString = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const fileName = `extraction_report_filtered_${dateTimeString}`;

        // Generate and download CSV file directly
        downloadCSVFile(dataWithTotals, fileName);
      } else {
        // Original flow for non-filtered data
        getReport(defaultUser, defaultOrganization)
          .then((result) => {
            if (result.success) {
              // Collect all possible keys from all items
              const allKeys = new Set();
              const dataList = result.data.map((item) => {
                let jsonData = {};
                try {
                  // Check if item.json exists and is not empty
                  if (item.json && typeof item.json === "string" && item.json.trim() !== "") {
                    jsonData = JSON.parse(item.json);
                  } else {
                    console.warn(`⚠️ View - Empty or missing JSON for item, using item data`);
                    jsonData = item;
                  }
                } catch (error) {
                  console.error(`❌ View - Failed to parse JSON:`, error);
                  console.error(`❌ View - Raw item.json:`, item.json);
                  jsonData = item;
                }

                // Extract total pages and forms with multiple fallback options
                const totalPages =
                  jsonData["Total Pages"] ||
                  jsonData["totalPages"] ||
                  jsonData.totalPage ||
                  item.totalPage ||
                  item.totalPages ||
                  "N/A";

                const totalForms =
                  jsonData["Total Forms"] ||
                  jsonData["totalForms"] ||
                  jsonData.totalForms ||
                  item.totalForms ||
                  item.totalForm ||
                  "N/A";

                // Extract type information and capitalize first letter
                const rawType =
                  jsonData["Type"] ||
                  jsonData.type ||
                  item.type ||
                  item.processType ||
                  item.extractionType ||
                  "extraction";

                const extractionType =
                  rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();

                return {
                  "File Name": item.fileName || "Unknown",
                  "Created By": item.createdBy || "Unknown",
                  "Created Date": formatDate(item.createdDate) || "Unknown",
                  "Organization Name": item.organizationName || "Unknown",
                  Type: extractionType,
                  "Total Pages": totalPages,
                  "Total Forms": totalForms,
                  ...Object.fromEntries(
                    Object.entries(jsonData || {}).filter(
                      ([key]) => key !== "Total Pages" && key !== "Total Forms"
                    )
                  ),
                };
              });

              // First pass to collect all possible keys
              dataList.forEach((item) => {
                Object.keys(item).forEach((key) => {
                  allKeys.add(key);
                });
              });

              // Format date fields in the data
              const formattedData = dataList.map((item) => {
                const formattedItem = {};

                // Ensure all keys are present in each item (for consistent columns)
                Array.from(allKeys).forEach((key) => {
                  // Format date fields
                  if (dateFields.has(key) && item[key]) {
                    formattedItem[key] = formatDate(item[key]);
                  } else {
                    formattedItem[key] = item[key] || ""; // Use empty string for missing values
                  }
                });

                return formattedItem;
              });

              // Calculate totals for data
              const totals = calculateTotals(formattedData);

              // Append totals row to the data
              const dataWithTotals = [...formattedData];
              if (totals) dataWithTotals.push(totals);

              // Create filename with current date and time
              const now = new Date();
              const dateTimeString = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
              const fileName = `extraction_report_${dateTimeString}`;

              // Generate and download CSV file directly
              downloadCSVFile(dataWithTotals, fileName);
            } else {
              setIsLoading(false);
              console.error("Failed to retrieve Extraction Report data");
            }
          })
          .catch((error) => {
            console.error("Error fetching Extraction Report:", error);
            setIsLoading(false);
          });
      }
    }
  };

  const handleBackToReports = () => {
    setViewingReport(false);
    setReportDataForView([]);
    setFilteredReportData([]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);

    // Apply search immediately or wait for user to finish typing
    if (e.target.value === "") {
      // Clear search results immediately when search box is emptied
      setFilteredReportData(reportDataForView);
    }
  };
  useEffect(() => {
    setFilteredReportData(filteredReportData);
  }, [filteredReportData]);

  // Modified filter function to search in both email and organization fields
  // Updated search filter function
  const filterReportData = () => {
    if (!searchTerm) {
      setFilteredReportData(reportDataForView);
      return;
    }
    console.log("Report Data For View:", reportDataForView);
    const lowercaseSearchTerm = searchTerm.toLowerCase();

    const filtered = reportDataForView.filter((item) => {
      // More comprehensive field checking for both report types
      return (
        // User Audit Report fields
        (item.email && item.email.toLowerCase().includes(lowercaseSearchTerm)) ||
        (item.userName && item.userName.toLowerCase().includes(lowercaseSearchTerm)) ||
        (item.fileName && item.fileName.toLowerCase().includes(lowercaseSearchTerm)) ||
        (item.organizationName &&
          item.organizationName.toLowerCase().includes(lowercaseSearchTerm)) ||
        // Extraction Report fields
        (item.EMAIL && item.EMAIL.toLowerCase().includes(lowercaseSearchTerm)) ||
        (item["Created By"] && item["Created By"].toLowerCase().includes(lowercaseSearchTerm)) ||
        (item["File Name"] && item["File Name"].toLowerCase().includes(lowercaseSearchTerm)) ||
        (item["Organization Name"] &&
          item["Organization Name"].toLowerCase().includes(lowercaseSearchTerm))
      );
    });

    setFilteredReportData(filtered);

    // Also update date-filtered data
    if (startDate && endDate) {
      setFilteredByDateData(filterDataByDateRange(filtered));
    } else {
      setFilteredByDateData(filtered);
    }
  };

  const groupDataByDate = (data) => {
    const grouped = {};
    data.forEach((item) => {
      // Check for both camelCase and spaced format date fields
      const date =
        item.createdDate ||
        item.uploadDate ||
        item.extractedDate ||
        item["Created Date"] ||
        item["Action Date"] ||
        item.actionDate;
      if (date) {
        const dateObj = new Date(date);
        const formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(dateObj.getDate()).padStart(2, "0")}`;

        if (!grouped[formattedDate]) {
          grouped[formattedDate] = [];
        }
        grouped[formattedDate].push(item);
      }
    });
    // Sort dates descending (latest first), and rows within each group by time descending
    const getItemTime = (item) =>
      new Date(
        item.createdDate ||
          item.uploadDate ||
          item.extractedDate ||
          item["Created Date"] ||
          item["Action Date"] ||
          item.actionDate ||
          0
      ).getTime();
    return Object.keys(grouped)
      .sort((a, b) => b.localeCompare(a))
      .reduce((sorted, key) => {
        sorted[key] = grouped[key].sort((a, b) => getItemTime(b) - getItemTime(a));
        return sorted;
      }, {});
  };

  // Sort rows latest-first by their date field (same order as the on-screen display)
  const sortLatestFirst = (arr) =>
    [...arr].sort((a, b) => {
      const ts = (item) =>
        new Date(
          item.actionDate || item.createdDate || item["Created Date"] || item["Action Date"] || 0
        ).getTime();
      return ts(b) - ts(a);
    });

  // Updated generatePDF function with logo and additional user info
  const generatePDF = (data, reportName) => {
    // Sort latest-first (mirrors on-screen display order)
    const sortedData = sortLatestFirst(data);
    const doc = new jsPDF();

    // Add logo
    const logoPath = "/logo.png";

    // Add logo (positioned at top left)
    doc.addImage(logoPath, "PNG", 14, 10, 50, 9);

    // Add title with correct report name (position adjusted to make room for logo)
    doc.setFontSize(16);
    doc.text(reportName, 14, 25);

    // Get user info from localStorage
    const userInfo = getUserIdFromLocalStorage();
    const name = `${userInfo.firstName} ${userInfo.lastName}`;
    const userName = name || "N/A";
    const userEmail = userInfo?.userName || "N/A";
    const organizationName = userInfo?.organizationName || "N/A";

    // Add user info
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    doc.text(`User: ${userName}`, 14, 35);
    doc.text(`Email: ${userEmail}`, 14, 40);
    doc.text(`Organization: ${organizationName}`, 14, 45);

    // Different columns based on report type
    const importantColumns =
      reportName === "User Audit Report"
        ? ["fileName", "userName", "email", "action", "actionDate"]
        : [
            "File Name",
            "Created By",
            "Created Date",
            "Organization Name",
            "Type",
            "Total Pages",
            "Total Forms",
          ];

    const headerNames =
      reportName === "User Audit Report"
        ? ["FILE NAME", "USER NAME", "EMAIL", "ACTION", "ACTION DATE"]
        : [
            "FILE NAME",
            "CREATED BY",
            "CREATED DATE",
            "ORGANIZATION NAME",
            "TYPE",
            "TOTAL PAGES",
            "TOTAL FORMS",
          ];

    // Prepare data for table - ensure data is properly formatted
    const tableData = sortedData.map((item) => {
      return importantColumns.map((col) => {
        // Check if the item has the column and it's not null or undefined
        if (item[col] !== undefined && item[col] !== null) {
          if (
            col === "action" &&
            typeof item[col] === "string" &&
            item[col].toLowerCase() === "exported"
          ) {
            return "Sent to Admin";
          }
          if (col.includes("Date") || col.includes("date")) {
            return formatDate(item[col]);
          }
          return String(item[col]); // Convert to string to ensure it displays properly
        }
        return ""; // Empty string for missing data
      });
    });

    // Add totals row for Extraction Report
    if (reportName === "Extraction Report") {
      const totals = calculateTotals(sortedData);
      if (totals) {
        const totalsRow = importantColumns.map((col) => {
          return totals[col] !== undefined ? String(totals[col]) : "";
        });
        tableData.push(totalsRow);
      }
    }

    // Add table with the properly formatted data - start at Y position 50 to make room for header information
    autoTable(doc, {
      head: [headerNames],
      body: tableData,
      startY: 50,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      footStyles: { fillColor: [238, 238, 238], fontStyle: "bold" },
    });

    return doc;
  };

  // Function to handle PDF download
  const handleDownloadPDF = (reportName) => {
    // Ensure we have the correct report name
    const actualReportName = viewingReportName || reportName;

    // Prioritize filtered data over full dataset
    let data = reportDataForView;

    if (
      Array.isArray(filteredReportData) &&
      filteredReportData.length > 0 &&
      filteredReportData.length !== reportDataForView.length
    ) {
      console.log("🔍 PDF - Using filtered data:", filteredReportData.length, "records");
      data = filteredReportData;
    } else if (
      Array.isArray(filteredByDateData) &&
      filteredByDateData.length > 0 &&
      filteredByDateData.length !== reportDataForView.length
    ) {
      console.log("🔍 PDF - Using date filtered data:", filteredByDateData.length, "records");
      data = filteredByDateData;
    } else {
      console.log("🔍 PDF - Using full dataset:", reportDataForView.length, "records");
    }

    // Make sure we have data
    if (!data || data.length === 0) {
      alert("No data available to generate PDF");
      return;
    }

    // Generate PDF with proper data
    const doc = generatePDF(data, actualReportName);
    doc.save(`${actualReportName}_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // Generate CSV content from array of objects (Excel-friendly)
  // SECURITY FIX: Includes CSV injection prevention (CWE-1236) — all cell values
  // are sanitized to prevent formula execution when opened in spreadsheet applications.
  const CSV_FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r", "\n"];
  const generateCSV = (data) => {
    if (!data || data.length === 0) return "";

    // Separate totals row (if any), sort the rest latest-first, then re-append totals
    const totalsRow = data.find(
      (row) => row && (row["File Name"] === "TOTALS" || row.fileName === "TOTALS")
    );
    const normalRows = data.filter(
      (row) => !row || (row["File Name"] !== "TOTALS" && row.fileName !== "TOTALS")
    );
    const sortedRows = sortLatestFirst(normalRows);
    const sortedData = totalsRow ? [...sortedRows, totalsRow] : sortedRows;

    // Build a stable ordered header from the union of keys across all rows
    const headerSet = new Set();
    sortedData.forEach((row) => Object.keys(row || {}).forEach((k) => headerSet.add(k)));
    const headers = Array.from(headerSet);

    // CSV escape with injection prevention
    const esc = (val) => {
      if (val === null || val === undefined) return "";
      let str = String(val);
      // Sanitize against CSV/formula injection — prefix dangerous characters with single quote
      if (str.length > 0 && CSV_FORMULA_TRIGGERS.some((t) => str.startsWith(t))) {
        str = "'" + str;
      }
      if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
      return str;
    };

    const lines = [];
    lines.push(headers.map(esc).join(","));
    sortedData.forEach((row) => {
      const line = headers.map((h) => {
        const val = row && row[h] !== undefined ? row[h] : "";
        // Map "exported" action to "Sent to Admin"
        if (h === "action" && typeof val === "string" && val.toLowerCase() === "exported") {
          return esc("Sent to Admin");
        }
        return esc(val);
      });
      lines.push(line.join(","));
    });
    return lines.join("\n");
  };

  // Update useEffect to group data when report data changes
  useEffect(() => {
    if (reportDataForView.length > 0) {
      const grouped = groupDataByDate(reportDataForView);
      setGroupedData(grouped);
      setSelectedDate(Object.keys(grouped)[0]);
    }
  }, [reportDataForView]);

  const filterDataByDateRange = (data) => {
    if (!startDate || !endDate) return data;

    return data.filter((item) => {
      // Check for both camelCase and spaced format date fields
      const itemDate = new Date(
        item.createdDate ||
          item.uploadDate ||
          item.actionDate ||
          item.extractedDate ||
          item["Created Date"] ||
          item["Action Date"]
      );
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  // Update filtered data when date range changes
  useEffect(() => {
    if (reportDataForView.length > 0) {
      const filtered = filterDataByDateRange(reportDataForView);
      setFilteredByDateData(filtered);
    }
  }, [startDate, endDate, reportDataForView]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div id="extraction_Review" className={screenHeaderStyle(isMenuOpen)}>
        {!viewingReport ? (
          <>
            {/* Table container */}
            <div
              style={{
                // margin: "20px",
                backgroundColor: "white",
                borderRadius: 8,
                overflow: "hidden",
                boxShadow: "none",
              }}
            >
              <div style={{ padding: "14px 20px 10px" }}></div>

              {/* Table header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "var(--table-header-bg)",
                  padding: "10px 20px",
                }}
              >
                <div style={{ flex: 1, fontFamily, fontSize: 12, fontWeight: 400, color: "#444" }}>
                  Report Name
                </div>
                {["PDF", "Excel"].map((label) => (
                  <div
                    key={label}
                    style={{
                      width: 40,
                      textAlign: "center",
                      fontFamily,
                      fontSize: 12,
                      fontWeight: 400,
                      color: "#444",
                    }}
                  >
                    {label}
                  </div>
                ))}
                {/* eye + trash header icons hidden (spacers) */}
                <div style={{ width: 30 }} />
                <div style={{ width: 30 }} />
              </div>

              {/* Rows */}
              {loading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LoaderIndicator />
                </div>
              ) : (
                rows.map((row, index) => (
                  <div
                    key={row.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 20px",
                      borderBottom: index < rows.length - 1 ? "1px solid #f0f0f0" : "none",
                      backgroundColor: "white",
                    }}
                  >
                    {/* Report name */}
                    <div style={{ flex: 1, fontFamily, fontSize: 13, color: "#222" }}>
                      {row.reportName}
                    </div>

                    {/* XML download */}
                    <div style={{ width: 40, display: "flex", justifyContent: "center" }}>
                      <img
                        src={downloadFileIcon}
                        alt="XML"
                        title="Download XML"
                        onClick={() => handlePdfReport(row.reportName)}
                        style={{
                          width: 22,
                          height: 22,
                          cursor: "pointer",
                          filter: "var(--icon-download-filter)",
                        }}
                      />
                    </div>

                    {/* Excel download */}
                    <div style={{ width: 40, display: "flex", justifyContent: "center" }}>
                      <img
                        src={downloadFileIcon}
                        alt="Excel"
                        title="Download Excel"
                        onClick={async () => await handleExcelReport(row.reportName)}
                        style={{
                          width: 22,
                          height: 22,
                          cursor: "pointer",
                          filter: "var(--icon-download-filter)",
                        }}
                      />
                    </div>

                    {/* View eye */}
                    <div style={{ width: 30, display: "flex", justifyContent: "center" }}>
                      <img
                        src={eyeIcon}
                        alt="View"
                        title="View Report"
                        onClick={() => handleViewReport(row.reportName)}
                        style={{
                          width: 20,
                          height: 20,
                          cursor: "pointer",
                          filter: "var(--icon-view-filter)",
                        }}
                      />
                    </div>

                    {/* Delete trash */}
                    <div style={{ width: 30, display: "flex", justifyContent: "center" }}>
                      <img
                        src={trashIcon}
                        alt="Delete"
                        title="Delete"
                        onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                        style={{
                          width: 20,
                          height: 20,
                          cursor: "pointer",
                          filter: "var(--icon-delete-filter)",
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            {/* Toolbar: back arrow + search + dates + download buttons */}
            <div
              style={{
                // margin: "20px 20px 0",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                backgroundColor: "white",
                borderRadius: "8px 8px 0 0",
                padding: "12px 16px",
                borderBottom: "1px solid #e8f4fb",
              }}
            >
              {/* Back arrow */}
              <img
                src={backArrowIcon}
                alt="Back"
                onClick={handleBackToReports}
                className="back-arrow-icon"
                style={{
                  width: 22,
                  height: 22,
                  marginRight: 4,
                }}
              />

              {/* Search */}
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                onClear={() => handleSearchChange({ target: { value: "" } })}
                placeholder="Search"
                style={{ flex: 1, minWidth: 160 }}
              />

              {/* Date range picker — dashboard style */}
              <button
                onClick={(e) => {
                  setPendingRange([startDate, endDate]);
                  setCalendarAnchor(e.currentTarget);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  height: 36,
                  minWidth: 160,
                  backgroundColor: "#fff",
                  border: "1px solid var(--report-btn-border-color)",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 400,
                  color: "#667085",
                  fontFamily,
                  marginLeft: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <img
                  src={calendarIcon}
                  alt="cal"
                  style={{ width: 16, height: 16, filter: "var(--icon-download-filter)" }}
                />
                <span style={{ flex: 1, textAlign: "left" }}>
                  {startDate && endDate
                    ? `${formatDateLabel(startDate)} – ${formatDateLabel(endDate)}`
                    : DATE_PRESETS.find((p) => p.value === datePreset)?.label || "All Time"}
                </span>
                <img src={downArrowIcon} alt="open" style={{ width: 10, height: 10 }} />
              </button>

              <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={() => setCalendarAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  style: {
                    borderRadius: 12,
                    boxShadow: "none",
                    overflow: "hidden",
                  },
                }}
              >
                {/* Preset chips */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    padding: "12px 16px 8px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handleDatePresetChange(preset.value)}
                      className={`report-preset-btn${datePreset === preset.value ? " report-preset-btn--active" : ""}`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                {/* Two static date pickers */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div style={{ display: "flex", borderTop: "1px solid #e0e0e0" }}>
                    <div style={{ borderRight: "1px solid #e0e0e0" }}>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#555",
                          paddingTop: 8,
                          fontFamily,
                        }}
                      >
                        Start Date
                      </div>
                      <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={pendingRange[0]}
                        onChange={(newDate) => {
                          const nr = [newDate, pendingRange[1]];
                          setPendingRange(nr);
                          if (newDate && pendingRange[1]) {
                            setDatePreset("custom");
                            setStartDate(newDate);
                            setEndDate(pendingRange[1]);
                            setCalendarAnchor(null);
                          }
                        }}
                        renderInput={() => null}
                        componentsProps={{ actionBar: { actions: [] } }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#555",
                          paddingTop: 8,
                          fontFamily,
                        }}
                      >
                        End Date
                      </div>
                      <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={pendingRange[1]}
                        onChange={(newDate) => {
                          const nr = [pendingRange[0], newDate];
                          setPendingRange(nr);
                          if (pendingRange[0] && newDate) {
                            setDatePreset("custom");
                            setStartDate(pendingRange[0]);
                            setEndDate(newDate);
                            setCalendarAnchor(null);
                          }
                        }}
                        renderInput={() => null}
                        componentsProps={{ actionBar: { actions: [] } }}
                      />
                    </div>
                  </div>
                </LocalizationProvider>
              </Popover>

              {/* Download Excel */}
              <Button
                onClick={() => handleDownloadExcelReport(viewingReportName, true)}
                variant="outlined"
                startIcon={<img src={excelIcon} alt="excel" style={{ width: 16, height: 16 }} />}
                style={{
                  borderColor: "var(--report-btn-border-color)",
                  color: "#667085",
                  fontFamily,
                  textTransform: "none",
                  fontSize: 12,
                  borderRadius: 7,
                  padding: "6px 14px",
                  whiteSpace: "nowrap",
                }}
              >
                Download Excel
              </Button>

              {/* Download PDF */}
              <Button
                onClick={() => handleDownloadPDF(viewingReportName || "User Audit Report")}
                variant="outlined"
                startIcon={<img src={pdfIcon} alt="pdf" style={{ width: 16, height: 16 }} />}
                style={{
                  borderColor: "var(--report-btn-border-color)",
                  color: "#667085",
                  fontFamily,
                  textTransform: "none",
                  fontSize: 12,
                  borderRadius: 7,
                  padding: "6px 14px",
                  whiteSpace: "nowrap",
                }}
              >
                Download PDF
              </Button>
            </div>

            {/* Report View Header + Data in one scroll container */}
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "0 0 8px 8px",
                maxHeight: "calc(100vh - 180px)",
                overflowY: "auto",
                position: "relative",
              }}
            >
              {/* Column header */}
              <div
                style={{
                  backgroundColor: "var(--table-header-bg)",
                  height: "44px",
                  fontFamily: fontFamily,
                  fontWeight: 400,
                  outline: "none",
                  fontSize: 13,
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  alignItems: "center",
                  display: "flex",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                {(viewingReportName === "User Audit Report"
                  ? viewAuditColumnsData
                  : viewExportedColumnsData
                ).map((column, index) => (
                  <div
                    title={column.headerName}
                    key={index}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      color: "#444",
                      marginLeft: "5px",
                      marginRight: "5px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {column.headerName}
                  </div>
                ))}
              </div>
              {/* Report Data Rows */}
              <div>
                {filteredByDateData.length > 0 ? (
                  <>
                    {/* Group data by date and display with date headers */}
                    {Object.entries(groupDataByDate(filteredByDateData)).map(([date, rows]) => (
                      <React.Fragment key={date}>
                        {/* Date header */}
                        <div
                          style={{
                            backgroundColor: "#f5f5f5",
                            padding: "8px 20px",
                            fontFamily: fontFamily,
                            fontWeight: 500,
                            fontSize: 13,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                            position: "sticky",
                            top: 44,
                            zIndex: 1,
                          }}
                        >
                          {formatDate(date)}
                        </div>

                        {/* Rows for this date */}
                        {rows.map((row, rowIndex) => (
                          <div
                            key={rowIndex}
                            style={{
                              backgroundColor: "white",
                              borderBottom: "1px solid #e0e0e0",
                              fontFamily: fontFamily,
                              fontWeight: 300,
                              fontSize: 12,
                              height: "44px",
                              display: "flex",

                              alignItems: "center",
                              paddingLeft: "20px",
                              paddingRight: "20px",
                            }}
                          >
                            {(viewingReportName === "User Audit Report"
                              ? viewAuditColumnsData
                              : viewExportedColumnsData
                            ).map((column, colIndex) => (
                              <div
                                title={
                                  column.field.includes("actionDate") && row[column.field]
                                    ? formatDate(row[column.field].toLowerCase())
                                    : row[column.field]
                                }
                                key={colIndex}
                                style={{
                                  flex: 1,
                                  textAlign: "left",
                                  marginLeft: "5px",
                                  height: "44px",
                                  marginRight: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {column.field.includes("actionDate") && row[column.field] ? (
                                  formatDate(row[column.field])
                                ) : column.field === "Total Pages" ||
                                  column.field === "Total Forms" ? (
                                  <div className="num-badge">{row[column.field]}</div>
                                ) : column.field === "action" &&
                                  typeof row[column.field] === "string" &&
                                  row[column.field].toLowerCase() === "exported" ? (
                                  "Sent to Admin"
                                ) : (
                                  row[column.field]
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}

                    {viewingReportName === "Extraction Report" && filteredByDateData.length > 0 && (
                      <div
                        style={{
                          backgroundColor: "#f9f9f9",
                          borderBottom: "1px solid #e0e0e0",
                          fontFamily: fontFamily,
                          fontWeight: 500,
                          fontSize: 12,
                          height: "44px",
                          display: "flex",
                          alignItems: "center",
                          // marginLeft: '20px',
                          // marginRight: '20px',
                          paddingLeft: "20px",
                          paddingRight: "20px",
                        }}
                      >
                        {viewExportedColumnsData.map((column, colIndex) => {
                          const totals = calculateTotals(filteredByDateData);
                          return (
                            <div
                              key={colIndex}
                              style={{
                                flex: 1,
                                textAlign: "left",
                                marginLeft: "5px",
                                height: "44px",
                                marginRight: "5px",
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {totals && totals[column.field] !== undefined ? (
                                column.field === "Total Pages" || column.field === "Total Forms" ? (
                                  <div className="num-badge">{totals[column.field]}</div>
                                ) : (
                                  totals[column.field]
                                )
                              ) : (
                                ""
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "20px",
                      fontFamily: fontFamily,
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    No data available for the selected date range
                  </div>
                )}
              </div>
            </div>
            {/* end scroll container */}
          </>
        )}

        {isLoading && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <LoaderIndicator />
          </div>
        )}
      </div>
      <PoweredByRow />
    </LocalizationProvider>
  );
}
